import React, { useState, useEffect } from 'react';
import { Reservation, Client, Accommodation, Minibar, StockItem, CompanyProfile } from '../types';
import BudgetCalculator from './BudgetCalculator';
import { calculateNights, formatCurrencyValue, formatLocalDate, asArray, asObject } from '../utils/storage';
import { jsPDF } from 'jspdf';
import { Skeleton } from './Skeleton';
import { 
  Calendar, 
  Plus, 
  Search, 
  Filter, 
  X, 
  Check, 
  AlertTriangle, 
  User, 
  Home, 
  Eye, 
  FileText, 
  DollarSign, 
  CreditCard,
  PlusCircle,
  FileDown,
  Sparkles
} from 'lucide-react';
import ReservationsTimeline from './ReservationsTimeline';
import TodayNotifications from './TodayNotifications';
import LowStockDashboard from './LowStockDashboard';

const getLocalDateString = (offsetDays = 0) => {
  const d = new Date();
  if (offsetDays !== 0) {
    d.setDate(d.getDate() + offsetDays);
  }
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getLocalTimeString = () => {
  const d = new Date();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

interface ReservasProps {
  reservations: Reservation[];
  clients: Client[];
  accommodations: Accommodation[];
  minibars: Minibar[];
  stockItems: StockItem[];
  profile: CompanyProfile;
  activeRole: string;
  onAddReservation: (res: Reservation) => void;
  onUpdateReservation: (id: string, updates: Partial<Reservation>) => void;
  onUpdateMinibarItems: (minibarId: string, itemId: string, itemQtyChange: number) => void;
  isLoading?: boolean;
}

export default function Reservas({
  reservations,
  clients,
  accommodations,
  minibars,
  stockItems,
  profile,
  activeRole,
  onAddReservation,
  onUpdateReservation,
  onUpdateMinibarItems,
  isLoading = false
}: ReservasProps) {
  // Filters & State
  const [activeTab, setActiveTab] = useState<'todos' | 'Pendente' | 'Iniciado' | 'Finalizado' | 'Cancelado'>('todos');
  const [viewMode, setViewMode] = useState<'list' | 'timeline'>('list');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);
  
  // Focused Reservation Details
  const [selectedResId, setSelectedResId] = useState<string | null>(null);
  const activeRes = reservations.find(r => r.id === selectedResId);

  // Form State: CHECK AVAILABILITY
  const [checkInDate, setCheckInDate] = useState(getLocalDateString());
  const [checkOutDate, setCheckOutDate] = useState(getLocalDateString(1));
  const [isAvailabilityChecked, setIsAvailabilityChecked] = useState(false);
  const [availableAccs, setAvailableAccs] = useState<Accommodation[]>([]);
  const [addResError, setAddResError] = useState('');

  // Form State: BOOKING DETAILS (Step 2)
  const [bookingAccId, setBookingAccId] = useState('');
  const [bookingClientId, setBookingClientId] = useState('');
  const [bookingGuestsCount, setBookingGuestsCount] = useState('2');
  const [bookingRate, setBookingRate] = useState('');
  const [bookingNotes, setBookingNotes] = useState('');

  // Check-In Form State
  const [checkInOption, setCheckInOption] = useState<'automatic' | 'manual'>('automatic');
  const [checkInManualDate, setCheckInManualDate] = useState(getLocalDateString());
  const [checkInManualTime, setCheckInManualTime] = useState(getLocalTimeString());

  // Consumption Form States (Minibar & Custom Additional)
  const [consItemId, setConsItemId] = useState('');
  const [consQty, setConsQty] = useState('1');
  const [consAddDesc, setConsAddDesc] = useState('');
  const [consAddValue, setConsAddValue] = useState('');
  const [consError, setConsError] = useState('');

  // Flows and inline confirmations states for iframe compatibility
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [checkInErrorMsg, setCheckInErrorMsg] = useState<string | null>(null);
  const [checkInForceAllow, setCheckInForceAllow] = useState(false);
  const [actionNotification, setActionNotification] = useState<string | null>(null);

  // Auto-dismiss notification effect
  useEffect(() => {
    if (actionNotification) {
      const timer = setTimeout(() => {
        setActionNotification(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [actionNotification]);

  // Check-Out Form States (Adicionais)
  const [extraCostsChecked, setExtraCostsChecked] = useState(false);
  const [tempExtraAmount, setTempExtraAmount] = useState('');
  const [tempExtraDesc, setTempExtraDesc] = useState('');
  const [addedExtrasList, setAddedExtrasList] = useState<{ id: string; description: string; amount: number }[]>([]);

  // Filter lists
  const filteredReservations = reservations.filter(res => {
    const clientName = clients.find(c => c.id === res.clientId)?.name || '';
    const accName = accommodations.find(a => a.id === res.accommodationId)?.name || '';
    const resCode = res.code || '';
    const matchesSearch = clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          resCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          accName.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (activeTab === 'todos') return matchesSearch;
    return matchesSearch && res.status === activeTab;
  });


  // Availability validation routine
  const handleVerifyAvailability = () => {
    if (!checkInDate || !checkOutDate) {
      setAddResError('Por favor, informe o período completo de hospedagem.');
      return;
    }
    if (checkInDate >= checkOutDate) {
      setAddResError('A data de entrada deve ser anterior à data de saída.');
      return;
    }

    // Filter active accommodations
    const activeAccsList = accommodations.filter(a => a.active);

    // Overlapping logic: check which accommodations are booked in requested days
    // A booking overlaps if: reservation is NOT Cancelled AND NOT Finished AND overlap exists
    // Overlap: checkInReserv < checkOutInput && checkOutReserv > checkInInput
    const overlappedAccIds = reservations
      .filter(r => r.status === 'Pendente' || r.status === 'Iniciado')
      .filter(r => {
        return r.checkInDate < checkOutDate && r.checkOutDate > checkInDate;
      })
      .map(r => r.accommodationId);

    const available = activeAccsList.filter(acc => !overlappedAccIds.includes(acc.id));
    setAvailableAccs(available);
    setIsAvailabilityChecked(true);
    setAddResError('');

    if (available.length > 0) {
      const first = available[0];
      setBookingAccId(first.id);
      setBookingRate(String(first.rate));
      // Preselect client if any
      const activeClients = clients.filter(c => c.active);
      if (activeClients.length > 0) {
        setBookingClientId(activeClients[0].id);
      }
    } else {
      setBookingAccId('');
      setBookingRate('');
    }
  };

  const handleAccChangeInBooking = (accId: string) => {
    setBookingAccId(accId);
    const acc = accommodations.find(a => a.id === accId);
    if (acc) {
      setBookingRate(String(acc.rate));
    }
  };

  const handleAddBookingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!bookingAccId || !bookingClientId || !bookingRate) {
      setAddResError('Por favor, preencha todos os campos obrigatórios (*).');
      return;
    }

    const matchedAcc = accommodations.find(a => a.id === bookingAccId);
    if (!matchedAcc) return;

    const capacityLimit = matchedAcc.capacity;
    const guests = parseInt(bookingGuestsCount);
    if (isNaN(guests) || guests <= 0 || guests > capacityLimit) {
      setAddResError(`Capacidade inválida! O quarto ${matchedAcc.number} comporta no máximo ${capacityLimit} hóspede(s).`);
      return;
    }

    const rateValue = parseFloat(bookingRate);
    if (isNaN(rateValue) || rateValue <= 0) {
      setAddResError('O valor da diária de estadia é inválido.');
      return;
    }

    // Create Reservation
    const newCode = `RES-${100 + reservations.length + 1}`;
    const newRes: Reservation = {
      id: `res_created_${Date.now()}`,
      code: newCode,
      accommodationId: bookingAccId,
      clientId: bookingClientId,
      checkInDate,
      checkOutDate,
      status: 'Pendente',
      guestsCount: guests,
      dailyRate: rateValue,
      notes: bookingNotes.trim(),
      consumptions: [],
      additionalCosts: [],
      totalStayAmount: 0,
      totalConsumptionAmount: 0,
      totalAdditionalAmount: 0,
      finalTotal: 0,
      createdAt: new Date().toISOString().split('T')[0]
    };

    onAddReservation(newRes);
    setShowAddModal(false);
    setIsAvailabilityChecked(false);
  };

  const updateReservationWithAudit = (id: string, updates: Partial<Reservation>) => {
    onUpdateReservation(id, { ...updates, updatedAt: new Date().toISOString(), updatedBy: activeRole });
  };

  // CHECK IN FUNCTION
  const triggerCheckInSubmit = () => {
    if (!activeRes) return;

    let dateInputStr = '';
    let timeInputStr = '';

    if (checkInOption === 'automatic') {
      dateInputStr = getLocalDateString();
      timeInputStr = getLocalTimeString();
    } else {
      dateInputStr = checkInManualDate;
      timeInputStr = checkInManualTime;
    }

    updateReservationWithAudit(activeRes.id, {
      status: 'Iniciado',
      checkInDetails: {
        realizedBy: activeRole === 'administrador' ? 'Administrador' : 'Recepcionista',
        date: dateInputStr,
        time: timeInputStr
      }
    });

    setShowCheckInModal(false);
    setCheckInErrorMsg(null);
    setCheckInForceAllow(false);
    setActionNotification(`Check-In concluído com sucesso para a Reserva ${activeRes.code}! Status alterado para Iniciado.`);
  };

  // ADD CONSUMPTION: MINIBAR
  const handleAddMinibarConsumption = () => {
    if (!activeRes || !consItemId || !consQty) {
      setConsError('Selecione um item e informe a quantidade.');
      return;
    }
    const qtyNum = parseInt(consQty);
    if (isNaN(qtyNum) || qtyNum <= 0) {
      setConsError('Quantidade deve ser positiva.');
      return;
    }

    // Get minibar associated with this accommodation
    const mbar = minibars.find(m => m.accommodationId === activeRes.accommodationId && m.active);
    if (!mbar) {
      setConsError('Nenhum frigobar disponível/ativo neste quarto.');
      return;
    }

    const minibarProduct = Array.isArray(mbar.items) ? mbar.items.find(i => i.itemId === consItemId) : null;
    if (!minibarProduct || minibarProduct.quantity < qtyNum) {
      setConsError(`O frigobar possui saldo insuficiente deste item (${minibarProduct?.quantity || 0} un disponíveis).`);
      return;
    }

    // Product reference to retrieve price
    const sItem = stockItems.find(i => i.id === consItemId);
    const price = sItem ? sItem.price : 5.0;

    // Log consumption
    const newConsumption = {
      id: `cons_${Date.now()}`,
      type: 'frigobar' as const,
      itemId: consItemId,
      description: sItem?.name || 'Item Frigobar',
      quantity: qtyNum,
      price: price,
      total: price * qtyNum,
      date: new Date().toISOString().replace('T', ' ').slice(0, 19)
    };

    // Deduct from minibar items list
    onUpdateMinibarItems(mbar.id, consItemId, -qtyNum);

    // Save on reservation
    const currentConsumptions = asArray(activeRes.consumptions);
    const updated = [...currentConsumptions, newConsumption];
    const totalConsSum = updated.reduce((sum, item) => sum + item.total, 0);

    updateReservationWithAudit(activeRes.id, {
      consumptions: updated,
      totalConsumptionAmount: totalConsSum,
      finalTotal: totalConsSum + activeRes.totalAdditionalAmount
    });

    setConsItemId('');
    setConsQty('1');
    setConsError('');
  };

  // ADD CONSUMPTION: CUSTOM ADDITIONAL
  const handleAddCustomConsumption = () => {
    if (!activeRes || !consAddDesc.trim() || !consAddValue) {
      setConsError('Preencha a descrição e valor do consumo adicional.');
      return;
    }
    const valNum = parseFloat(consAddValue);
    if (isNaN(valNum) || valNum <= 0) {
      setConsError('Valor inválido.');
      return;
    }

    const newConsumption = {
      id: `cons_${Date.now()}`,
      type: 'adicional' as const,
      description: consAddDesc.trim(),
      quantity: 1,
      price: valNum,
      total: valNum,
      date: new Date().toISOString().replace('T', ' ').slice(0, 19)
    };

    const currentConsumptions = asArray(activeRes.consumptions);
    const updated = [...currentConsumptions, newConsumption];
    const totalConsSum = updated.reduce((sum, item) => sum + item.total, 0);

    updateReservationWithAudit(activeRes.id, {
      consumptions: updated,
      totalConsumptionAmount: totalConsSum,
      finalTotal: totalConsSum + activeRes.totalAdditionalAmount
    });

    setConsAddDesc('');
    setConsAddValue('');
    setConsError('');
  };

  // TRIGGER CHECK-OUT FLOW SCREEN
  const openCheckOutModalAction = () => {
    if (!activeRes) return;
    setAddedExtrasList([]);
    setTempExtraAmount('');
    setTempExtraDesc('');
    setExtraCostsChecked(false);
    setShowCheckOutModal(true);
  };

  const handleAddCheckOutExtra = () => {
    const amt = parseFloat(tempExtraAmount);
    if (isNaN(amt) || amt <= 0 || !tempExtraDesc.trim()) {
      alert('Informe a descrição e o valor do custo opcional.');
      return;
    }
    setAddedExtrasList(prev => [
      ...prev,
      { id: `ext_${Date.now()}`, description: tempExtraDesc.trim(), amount: amt }
    ]);
    setTempExtraDesc('');
    setTempExtraAmount('');
  };

  const handleRemoveCheckOutExtra = (idx: number) => {
    setAddedExtrasList(prev => prev.filter((_, i) => i !== idx));
  };

  // COMMIT CHECK-OUT AND GENERATE PDF RECEIPT
  const handleConfirmPaymentAndCheckout = () => {
    if (!activeRes) return;

    // Calculations
    const checkInDateActual = activeRes.checkInDetails?.date || activeRes.checkInDate;
    const checkOutToday = getLocalDateString(); // real current checkout date
    const currentCheckoutTime = getLocalTimeString(); // real current checkout time
    const elapsedNights = calculateNights(checkInDateActual, checkOutToday);
    const subtotalStay = elapsedNights * activeRes.dailyRate;
    const subtotalCons = activeRes.totalConsumptionAmount;
    const subtotalExtra = addedExtrasList.reduce((sum, i) => sum + i.amount, 0);
    const calculatedGrandTotal = subtotalStay + subtotalCons + subtotalExtra;

    // Apply Finished Status
    updateReservationWithAudit(activeRes.id, {
      status: 'Finalizado',
      checkOutDateActual: checkOutToday,
      checkOutTimeActual: currentCheckoutTime,
      additionalCosts: addedExtrasList,
      totalStayAmount: subtotalStay,
      totalAdditionalAmount: subtotalExtra,
      finalTotal: calculatedGrandTotal
    });

    setShowCheckOutModal(false);
    setShowDetailModal(false);

    alert('Checkout e Pagamento concluídos com sucesso! Baixando comprovante PDF...');

    // Now, automatically trigger Receipt PDF generation!
    generateCheckoutPDF(activeRes.id, checkOutToday, elapsedNights, subtotalStay, subtotalCons, addedExtrasList, calculatedGrandTotal);
  };

  // CHECKOUT PDF GENERATOR
  const generateCheckoutPDF = (
    resId: string, 
    checkoutDate: string,
    nights: number, 
    staySub: number, 
    consSub: number, 
    extras: { description: string; amount: number }[],
    grandTotal: number
  ) => {
    const res = reservations.find(r => r.id === resId) || activeRes;
    if (!res) return;

    try {
      const doc = new jsPDF();
      const guest = clients.find(c => c.id === res.clientId);
      const room = accommodations.find(a => a.id === res.accommodationId);

      // PDF Slate Design layout
      doc.setFillColor(24, 24, 27); // Zinc-900 (Dark Slate Header)
      doc.rect(0, 0, 210, 45, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text(profile.name || "Pousada Recanto do Sol", 15, 14);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(212, 212, 216); // Zinc-300
      doc.text(`Razão Social: ${profile.corporateName || "F. M. Dos Santos Hospedagens LTDA"} | CNPJ: ${profile.document || "28.190.283/0001-92"}`, 15, 20);
      doc.text(`Localização: ${profile.address || "Rua das Conchas, 145 - Centro, Búzios - RJ"}`, 15, 25);
      doc.text(`Telefone: ${profile.phone} | Contato: ${profile.email}`, 15, 30);
      doc.text(`Chave PIX para pagamentos: ${profile.pixKey}`, 15, 35);

      // Receipt Badge Right Side
      doc.setFillColor(39, 39, 42); // Zinc-800
      doc.rect(140, 8, 55, 29, 'F');
      doc.setDrawColor(63, 63, 70); // Zinc-700
      doc.rect(140, 8, 55, 29, 'D');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8.5);
      doc.setFont("helvetica", "bold");
      doc.text("RECIBO OPERACIONAL", 167, 14, { align: 'center' });
      doc.text("COMPROVANTE DE ESTADIA", 167, 18, { align: 'center' });

      doc.setFontSize(11);
      doc.setTextColor(234, 179, 8); // Gold text
      doc.text(res.code, 167, 26, { align: 'center' });
      doc.setFontSize(7.5);
      doc.setTextColor(161, 161, 170);
      doc.text(`Data: ${formatLocalDate(checkoutDate)}`, 167, 32, { align: 'center' });

      // Reset text options
      doc.setTextColor(24, 24, 27); // Zinc-900
      let currentY = 58;

      // Guest metadata section
      doc.setFillColor(244, 244, 245); // Zinc-100
      doc.rect(15, currentY, 180, 26, 'F');
      
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(63, 63, 70);
      doc.text("INFORMAÇÕES DO CLIENTE E DA HOSPEDAGEM", 20, currentY + 6);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(`Nome completo: ${guest?.name || 'Carregando...'}`, 20, currentY + 12);
      doc.text(`Documento CPF: ${guest?.document || 'N/D'}`, 20, currentY + 17);
      doc.text(`Período de Estadia: de ${formatLocalDate(res.checkInDate)} até ${formatLocalDate(res.checkOutDate)}`, 20, currentY + 22);

      // Accommodation right side info
      doc.text(`Acomodação: ${room?.name || 'Sem especificação'}`, 115, currentY + 12);
      doc.text(`Acomodação: ${room?.name || 'Não informada'} (Nº ${room?.number || 'N/A'})`, 115, currentY + 17);
      doc.text(`Diária Contratada: ${formatCurrencyValue(res.dailyRate)} (Nº Hóspedes: ${res.guestsCount})`, 115, currentY + 22);

      currentY += 34;

      // Financial columns
      doc.setFillColor(244, 244, 245);
      doc.rect(15, currentY, 180, 7, 'F');
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text("CONTA DETALHADA E DEMONSTRATIVO FINANCEIRO", 18, currentY + 5);
      currentY += 7;

      // 1. Stay table rows
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.text("PRODUTOS / SERVIÇOS DE HOSPEDAGEM", 18, currentY + 5);
      doc.text("QUANTIDADE", 100, currentY + 5);
      doc.text("VALOR UNITÁRIO", 137, currentY + 5);
      doc.text("SUBTOTAL COBRADO", 192, currentY + 5, { align: 'right' });
      
      doc.setDrawColor(228, 228, 231);
      doc.line(15, currentY + 7, 195, currentY + 7);
      currentY += 7;

      doc.setFont("helvetica", "normal");
      doc.text(`Estadia em ${room?.name || 'Acomodação não identificada'}`, 18, currentY + 5);
      doc.text(`${nights} noites`, 100, currentY + 5);
      doc.text(formatCurrencyValue(res.dailyRate), 137, currentY + 5);
      doc.text(formatCurrencyValue(staySub), 192, currentY + 5, { align: 'right' });
      currentY += 8;

      // 2. Consumption table rows
      const consumptions = asArray(res.consumptions);
      if (consumptions.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.text("ITENS DE CONSUMO INTERNO (MINIBAR / FRIGOBAR & EXTRAS)", 18, currentY + 4);
        currentY += 6;

        consumptions.forEach(item => {
          doc.setFont("helvetica", "normal");
          const typeStr = item.type === 'frigobar' ? '[Frigobar]' : '[Consumo]';
          // Trim description to avoid text bleeding
          const descStr = `${typeStr} ${item.description}`;
          const trimmedDesc = descStr.length > 40 ? descStr.slice(0, 38) + '..' : descStr;
          doc.text(trimmedDesc, 18, currentY + 4);
          doc.text(`${item.quantity} un`, 100, currentY + 4);
          doc.text(formatCurrencyValue(item.price), 137, currentY + 4);
          doc.text(formatCurrencyValue(item.total), 192, currentY + 4, { align: 'right' });
          currentY += 6.5;
        });
        currentY += 3;
      }

      // 3. Extras items
      const resolvedExtras = extras.length > 0 ? extras : asArray(res.additionalCosts);
      if (resolvedExtras.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.text("OUTRAS TAXAS & DESPESAS ADICIONAIS", 18, currentY + 4);
        currentY += 6;

        resolvedExtras.forEach(item => {
          doc.setFont("helvetica", "normal");
          doc.text(`[Acordo] ${item.description}`, 18, currentY + 4);
          doc.text("1 un", 100, currentY + 4);
          doc.text(formatCurrencyValue(item.amount), 137, currentY + 4);
          doc.text(formatCurrencyValue(item.amount), 192, currentY + 4, { align: 'right' });
          currentY += 6.5;
        });
        currentY += 3;
      }

      // 4. Totals Block Box
      currentY += 5;
      if (currentY > 230) {
        doc.addPage();
        currentY = 20;
      }

      doc.setFillColor(244, 244, 245);
      doc.rect(130, currentY, 65, 30, 'F');
      doc.setDrawColor(212, 212, 216);
      doc.rect(130, currentY, 65, 30, 'D');

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.text("Subtotal Estadia:", 135, currentY + 7);
      doc.text(formatCurrencyValue(staySub), 190, currentY + 7, { align: 'right' });

      doc.text("Consumo Frigobar:", 135, currentY + 12);
      doc.text(formatCurrencyValue(consSub), 190, currentY + 12, { align: 'right' });

      const extraSum = resolvedExtras.reduce((sum, item) => sum + item.amount, 0);
      doc.text("Custos Adicionais:", 135, currentY + 17);
      doc.text(formatCurrencyValue(extraSum), 190, currentY + 17, { align: 'right' });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.setTextColor(24, 24, 27);
      doc.text("VALOR TOTAL:", 135, currentY + 25);
      doc.text(formatCurrencyValue(grandTotal), 190, currentY + 25, { align: 'right' });

      // Signature lines
      currentY += 45;
      doc.setDrawColor(161, 161, 170);
      doc.line(25, currentY, 90, currentY);
      doc.line(120, currentY, 185, currentY);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(113, 113, 122);
      doc.text("Responsável Recepção (Assinatura)", 57, currentY + 4, { align: 'center' });
      doc.text(`${guest?.name || 'Cliente'} (Hóspede)`, 152, currentY + 4, { align: 'center' });

      // Save document
      doc.save(`financeiro_recibo_${res.code}_checkout_sol.pdf`);
    } catch (err) {
      console.error(err);
      alert('Erro ao gerar documento do comprovante em PDF.');
    }
  };

  return (
    <div className="space-y-6">
      {actionNotification && (
        <div className="p-4 bg-zinc-900 text-white rounded-xl flex items-center justify-between text-xs font-semibold shadow-md animate-fade-in border border-zinc-800">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500 shrink-0" />
            <span>{actionNotification}</span>
          </div>
          <button
            type="button"
            onClick={() => setActionNotification(null)}
            className="text-zinc-400 hover:text-white font-extrabold focus:outline-none cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Title Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-200 pb-5">
        <div>
          <h1 id="header-title-reservations" className="text-xl font-semibold tracking-tight text-zinc-950">
            Painel Geral de Reservas & Estadias
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Cadastre estadias, consulte acomodações desocupadas, emita termos operacionais de check-in / check-out e valide contas detalhadas.
          </p>
        </div>
        <div>
          <button
            type="button"
            id="btn-add-booking-modal-trigger"
            onClick={() => {
              setCheckInDate('2026-06-05');
              setCheckOutDate('2026-06-12');
              setIsAvailabilityChecked(false);
              setBookingAccId('');
              setBookingClientId('');
              setBookingGuestsCount('2');
              setBookingRate('');
              setBookingNotes('');
              setAddResError('');
              setShowAddModal(true);
            }}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg text-xs font-bold uppercase tracking-wider transition-all cursor-pointer shadow-sm active:scale-[0.98]"
          >
            <Plus className="w-4 h-4" /> Nova Reserva
          </button>
        </div>
      </div>

      {/* Categories buttons for filtering STATUS, matching page 11 */}
      <div className="bg-white p-4 border border-zinc-200 rounded-xl shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Reservation Status Badges */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0 w-full md:w-auto">
          <button
            type="button"
            id="tab-res-all"
            onClick={() => setActiveTab('todos')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
              activeTab === 'todos' ? 'bg-zinc-900 text-white' : 'text-zinc-650 hover:bg-zinc-100'
            }`}
          >
            Todas ({reservations.length})
          </button>
          
          <button
            type="button"
            id="tab-res-pendente"
            onClick={() => setActiveTab('Pendente')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap cursor-pointer transition-all border-l-3 ${
              activeTab === 'Pendente' 
                ? 'bg-amber-100 text-amber-900 border-amber-500 font-bold' 
                : 'text-zinc-600 bg-amber-50/40 border-amber-300 hover:bg-amber-100/50'
            }`}
          >
            Pendentes ({reservations.filter(r => r.status === 'Pendente').length})
          </button>

          <button
            type="button"
            id="tab-res-iniciadas"
            onClick={() => setActiveTab('Iniciado')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap cursor-pointer transition-all border-l-3 ${
              activeTab === 'Iniciado' 
                ? 'bg-blue-100 text-blue-900 border-blue-550 font-bold' 
                : 'text-zinc-600 bg-blue-50/40 border-blue-300 hover:bg-blue-100/50'
            }`}
          >
            Iniciadas ({reservations.filter(r => r.status === 'Iniciado').length})
          </button>

          <button
            type="button"
            id="tab-res-finalizado"
            onClick={() => setActiveTab('Finalizado')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap cursor-pointer transition-all border-l-3 ${
              activeTab === 'Finalizado' 
                ? 'bg-green-100 text-green-900 border-green-550 font-bold' 
                : 'text-zinc-650 bg-green-50/40 border-green-200 hover:bg-green-100/50'
            }`}
          >
            Finalizadas ({reservations.filter(r => r.status === 'Finalizado').length})
          </button>

          <button
            type="button"
            id="tab-res-cancelado"
            onClick={() => setActiveTab('Cancelado')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap cursor-pointer transition-all border-l-3 ${
              activeTab === 'Cancelado' 
                ? 'bg-red-100 text-red-900 border-red-500 font-bold' 
                : 'text-zinc-650 bg-red-50/40 border-red-200 hover:bg-red-100/50'
            }`}
          >
            Canceladas ({reservations.filter(r => r.status === 'Cancelado').length})
          </button>
        </div>

        {/* Input search bar / Search code booking, matching page 11 */}
        <div className="flex items-center gap-2 w-full md:w-80">
          <input
            type="text"
            id="search-res-bar-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="text-xs border border-zinc-200 rounded-lg p-2 flex-1 focus:outline-none focus:bg-white bg-zinc-50/50"
            placeholder="Pesquisar Nº da reserva..."
          />
          <button
            type="button"
            id="btn-trigger-search-res"
            className="bg-zinc-900 text-white text-xs px-4 py-2 font-semibold uppercase rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
          >
            Buscar
          </button>
        </div>
      </div>

      {/* Notifications */}
      <LowStockDashboard stockItems={stockItems} />
      <TodayNotifications reservations={reservations} />

      {/* View Mode Toggle */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-1.5 p-1 bg-zinc-100 rounded-lg mb-2">
          <button
            type="button"
            onClick={() => setViewMode('list')}
            className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase transition-all ${
              viewMode === 'list' 
                ? 'bg-white text-zinc-900 shadow-sm' 
                : 'text-zinc-500 hover:text-zinc-900'
            }`}
          >
            Lista
          </button>
          <button
            type="button"
            onClick={() => setViewMode('timeline')}
            className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase transition-all ${
              viewMode === 'timeline' 
                ? 'bg-white text-zinc-900 shadow-sm' 
                : 'text-zinc-500 hover:text-zinc-900'
            }`}
          >
            Linha do Tempo
          </button>
        </div>
      </div>


      {/* Grid Cards of Reservations or Timeline */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-48 w-full rounded-xl" />
          ))}
        </div>
      ) : viewMode === 'list' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filteredReservations.length === 0 ? (
            <div id="res-grid-empty" className="col-span-full bg-white p-12 text-center text-zinc-400 border border-zinc-200 rounded-xl leading-normal font-normal">
              Nenhuma reserva correspondente localizada para os filtros selecionados.
            </div>
          ) : (
            filteredReservations.map(res => {
              const guest = clients.find(c => c.id === res.clientId);
              const acc = accommodations.find(a => a.id === res.accommodationId);

              // Select background status bars
              let colorBarClass = 'bg-zinc-400';
              if (res.status === 'Pendente') colorBarClass = 'bg-yellow-500'; // amarela (pendente)
              if (res.status === 'Iniciado') colorBarClass = 'bg-blue-500'; // azul (estadia ativa)
              if (res.status === 'Finalizado') colorBarClass = 'bg-green-600'; // verde (pago)
              if (res.status === 'Cancelado') colorBarClass = 'bg-red-500'; // vermelho (cancelado)

              return (
                <div
                  key={res.id}
                  id={`reservation-card-${res.id}`}
                  className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                >
                  {/* Horizontal color status representation bar */}
                  <div className={`h-1.5 w-full ${colorBarClass}`} />

                  <div className="p-4 flex-1 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs font-extrabold text-zinc-900 bg-zinc-100 px-2 py-0.5 rounded border border-zinc-200/50">
                        Nº {res.code}
                      </span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        res.status === 'Pendente' ? 'bg-amber-50 text-amber-900' :
                        res.status === 'Iniciado' ? 'bg-blue-50 text-blue-900' :
                        res.status === 'Finalizado' ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-900'
                      }`}>
                        {res.status}
                      </span>
                    </div>

                    {/* Acc Details */}
                    <div className="space-y-1">
                      <div className="font-semibold text-zinc-900 text-xs flex items-center gap-1">
                        <Home className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                        <span>{acc?.name || 'Acomodação Inativa'}</span>
                      </div>
                      <div className="text-xxs text-zinc-400">Quarto {acc?.number || 'N/A'} - {res.guestsCount} Hóspedes</div>
                    </div>

                    {/* Guest details */}
                    <div className="space-y-0.5 pt-1.5 border-t border-zinc-100">
                      <span className="block text-[9px] font-bold text-zinc-400 uppercase tracking-wide">Responsável da Ficha</span>
                      <span className="font-bold text-zinc-800 text-xs truncate block">{guest?.name || 'Não cadastrado'}</span>
                      <span className="font-mono text-[10px] text-zinc-500 block">CPF: {guest?.document || 'N/D'}</span>
                    </div>

                    {/* Period Calendar */}
                    <div className="text-xs pt-2 text-zinc-650 flex items-center gap-1.5 font-semibold">
                      <Calendar className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                      <span>{formatLocalDate(res.checkInDate)} - {formatLocalDate(res.checkOutDate)}</span>
                    </div>
                  </div>

                  <div className="bg-zinc-50 px-4 py-3 border-t border-zinc-100 flex items-center justify-between">
                    <span className="font-mono text-xxs font-bold text-zinc-400">Registrado em {formatLocalDate(res.createdAt)}</span>
                    <button
                      type="button"
                      id={`btn-open-res-detail-${res.id}`}
                      onClick={() => {
                        setSelectedResId(res.id);
                        setShowDetailModal(true);
                      }}
                      className="flex items-center gap-1 px-3 py-1.5 border border-zinc-200 hover:border-zinc-300 text-zinc-800 bg-white hover:bg-zinc-100 text-xxs font-bold uppercase tracking-wider rounded-lg shadow-sm cursor-pointer transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5 text-zinc-500" /> Visualizar
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        <ReservationsTimeline reservations={reservations} accommodations={accommodations} />
      )}

      {/* Modal: NEW RESERVATION STEP MANAGER (availability checks, matching Page 10) */}
      {showAddModal && (
        <div id="add-reservation-modal" className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto">
          <div className="bg-white border border-zinc-200 shadow-xl rounded-xl max-w-lg lg:max-w-2xl xl:max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden transform transition-all my-auto">
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-zinc-300" />
                <h3 className="text-sm font-bold uppercase tracking-wider">Agendar / reservar uma acomodação</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="text-zinc-400 hover:text-white cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-5 overflow-y-auto flex-1">
              {addResError && (
                <div className="p-2.5 bg-red-100 border border-red-200 text-red-800 text-xs rounded-lg font-medium leading-normal">
                  {addResError}
                </div>
              )}

              {/* Step 1: Range checks */}
              <div className="p-4 bg-zinc-50 border border-zinc-200/60 rounded-xl space-y-4">
                <span className="block text-xxs font-bold text-zinc-450 uppercase tracking-wider">Passo 1: Definir período e pesquisar disponibilidade</span>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="form-res-checkin" className="block text-xxs font-bold text-zinc-500 uppercase mb-1">Data Check-In Range *</label>
                    <input
                      type="date"
                      id="form-res-checkin"
                      value={checkInDate}
                      onChange={(e) => {
                        setCheckInDate(e.target.value);
                        setIsAvailabilityChecked(false);
                      }}
                      className="w-full text-xs p-2 border border-zinc-200 rounded-lg bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="form-res-checkout" className="block text-xxs font-bold text-zinc-500 uppercase mb-1">Até (Check-Out) *</label>
                    <input
                      type="date"
                      id="form-res-checkout"
                      value={checkOutDate}
                      onChange={(e) => {
                        setCheckOutDate(e.target.value);
                        setIsAvailabilityChecked(false);
                      }}
                      className="w-full text-xs p-2 border border-zinc-200 rounded-lg bg-white"
                      required
                    />
                  </div>
                </div>

                <button
                  type="button"
                  id="btn-verify-availability"
                  onClick={handleVerifyAvailability}
                  className="w-full py-2 bg-green-700 hover:bg-green-600 text-white font-bold text-xs uppercase tracking-wider rounded-lg cursor-pointer transition-all active:scale-[0.98] shadow-sm shadow-green-950/10"
                >
                  verificar disponibilidade
                </button>
              </div>

              {/* Step 2: Book form matches page 10 screenshot if check is made */}
              {isAvailabilityChecked && (
                <form onSubmit={handleAddBookingSubmit} className="space-y-4 animate-fade-in border-t border-zinc-150 pt-4">
                  <div className="flex items-center gap-1.5 p-2 bg-green-50 text-green-800 border border-green-200 rounded-lg text-xxs font-bold tracking-wide">
                    <Sparkles className="w-4 h-4 text-green-700 font-bold" />
                    <span>{availableAccs.length} acomodações disponíveis encontradas para este período!</span>
                  </div>

                  {availableAccs.length === 0 ? (
                    <div className="p-3 bg-red-50 text-red-800 border border-red-100 rounded-lg text-xs leading-normal">
                      Infelizmente todos os quartos e chalés encontram-se reservados no período solicitado. Tente outra data.
                    </div>
                  ) : (
                    <>
                      {/* Accommodation select list */}
                      <div>
                        <label htmlFor="form-acc-res-select" className="block text-xxs font-bold text-zinc-550 uppercase mb-1">Acomodações disponíveis *</label>
                        <select
                          id="form-acc-res-select"
                          value={bookingAccId}
                          onChange={(e) => handleAccChangeInBooking(e.target.value)}
                          className="w-full text-xs p-2 border border-zinc-200 rounded-lg font-semibold"
                          required
                        >
                          {availableAccs.map(acc => (
                            <option key={acc.id} value={acc.id}>
                              {acc.name} (Quarto {acc.number}) - {formatCurrencyValue(acc.rate)}/diária
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Calculator */}
                      <BudgetCalculator checkInDate={checkInDate} checkOutDate={checkOutDate} rate={parseFloat(bookingRate) || 0} />

                      {/* Customer list */}
                      <div>
                        <label htmlFor="form-res-client-id" className="block text-xxs font-bold text-zinc-550 uppercase mb-1">Hóspede (Nome e CPF) *</label>
                        <select
                          id="form-res-client-id"
                          value={bookingClientId}
                          onChange={(e) => setBookingClientId(e.target.value)}
                          className="w-full text-xs p-2 border border-zinc-200 rounded-lg font-semibold"
                          required
                        >
                          <option value="" disabled>--- Selecione o hóspede ---</option>
                          {clients.filter(c => c.active).map(client => (
                            <option key={client.id} value={client.id}>
                              {client.name} - CPF: {client.document || 'N/A'}
                            </option>
                          ))}
                        </select>
                        <span className="text-[10px] text-zinc-400 block mt-1">Apenas clientes com cadastro operacional ATIVO são listados.</span>
                      </div>

                      {/* Observações */}
                      <div>
                        <label htmlFor="form-res-notes" className="block text-xxs font-bold text-zinc-550 uppercase mb-1">Observação</label>
                        <textarea
                          id="form-res-notes"
                          value={bookingNotes}
                          onChange={(e) => setBookingNotes(e.target.value)}
                          className="w-full text-xs p-2 border border-zinc-200 rounded-lg bg-zinc-50/20"
                          rows={2}
                          placeholder="Detalhes ou observações da reserva..."
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Guests count */}
                        <div>
                          <label htmlFor="form-res-guests" className="block text-xxs font-bold text-zinc-550 uppercase mb-1">Número de Hóspedes (Máx. 2)</label>
                          <input
                            type="number"
                            id="form-res-guests"
                            value={bookingGuestsCount}
                            onChange={(e) => setBookingGuestsCount(e.target.value)}
                            min={1}
                            className="w-full text-xs p-2 border border-zinc-200 rounded-lg text-center"
                            required
                          />
                        </div>

                        {/* Daily Rate Value */}
                        <div>
                          <label htmlFor="form-res-rate-custom" className="block text-xxs font-bold text-zinc-550 uppercase mb-1">Valor da Estadia (Editable) *</label>
                          <input
                            type="number"
                            step="0.01"
                            id="form-res-rate-custom"
                            value={bookingRate}
                            onChange={(e) => setBookingRate(e.target.value)}
                            className="w-full text-xs p-2 border border-zinc-200 rounded-lg text-center font-bold"
                            required
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4 mt-6">
                        <button
                          type="button"
                          onClick={() => setShowAddModal(false)}
                          className="px-4 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-lg text-zinc-700 font-semibold text-xs cursor-pointer"
                        >
                          Cancelar
                        </button>
                        <button
                          type="submit"
                          id="btn-reservation-commit-submit"
                          className="px-5 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider cursor-pointer shadow-sm"
                        >
                          Cadastrar
                        </button>
                      </div>
                    </>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: DETAILED RESERVATION INSPECTOR (Check-in status, add consumptions, checkout details, matching screenshots) */}
      {showDetailModal && activeRes && (
        <div id="detail-reservation-modal" className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-40 overflow-y-auto">
          <div className="bg-white border border-zinc-200 shadow-xl rounded-xl max-w-4xl xl:max-w-6xl w-full max-h-[92vh] flex flex-col overflow-hidden transform transition-all my-auto">
            {/* Header detail */}
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <span className="font-mono text-sm font-extrabold text-zinc-200 bg-zinc-800 px-3 py-1 rounded">
                Reserva {activeRes.code}
              </span>
              <button
                type="button"
                onClick={() => setShowDetailModal(false)}
                className="text-zinc-400 hover:text-white cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-6 overflow-y-auto flex-1">
              {/* Box 1: Core Details summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-zinc-50 border border-zinc-200/60 rounded-xl">
                <div>
                  <span className="block text-[10px] font-bold text-zinc-400 uppercase">Hóspede</span>
                  <span className="text-xs font-bold text-zinc-900 block truncate">
                    {clients.find(c => c.id === activeRes.clientId)?.name || 'Carregando...'}
                  </span>
                </div>
                <div>
                  <span className="block text-[10px] font-bold text-zinc-400 uppercase">Acomodação</span>
                  <span className="text-xs font-semibold text-zinc-800 block">
                    {accommodations.find(a => a.id === activeRes.accommodationId)?.name || 'Incompleto'}
                  </span>
                </div>
                <div>
                  <span className="block text-[10px] font-bold text-zinc-400 uppercase">Período Previsto</span>
                  <span className="text-xs font-normal text-zinc-600 block">
                    {formatLocalDate(activeRes.checkInDate)} a {formatLocalDate(activeRes.checkOutDate)}
                  </span>
                </div>
                <div>
                  <span className="block text-[10px] font-bold text-zinc-400 uppercase">Diária da Estadia</span>
                  <span className="text-xs font-bold text-zinc-900 block">
                    {formatCurrencyValue(activeRes.dailyRate)} (Nº hóspedes: {activeRes.guestsCount})
                  </span>
                </div>
              </div>

              {/* Status Actions based on manual Page 11 check-in flow */}
              {activeRes.status === 'Pendente' && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-amber-50 border border-amber-200/50 rounded-xl">
                  <div className="flex items-start gap-2.5">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <span className="text-xs font-bold text-amber-900 block">Estadia Pendente de Check-In</span>
                      <span className="text-xxs text-amber-700 block mt-0.5 leading-normal">
                        Esta reserva foi cadastrada operativamente no sistema com sucesso. Clique em Realizar Check-in para habilitar estadias e consumos.
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 shrink-0 items-center">
                    {showCancelConfirm ? (
                      <div className="flex items-center gap-2.5 p-2 bg-red-50 border border-red-200 rounded-lg animate-fade-in shadow-xs">
                        <span className="text-[10px] font-extrabold text-red-950 uppercase tracking-tight">Realmente cancelar?</span>
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              onUpdateReservation(activeRes.id, { status: 'Cancelado' });
                              setShowDetailModal(false);
                              setShowCancelConfirm(false);
                              setActionNotification(`A Reserva ${activeRes.code} foi cancelada com sucesso operativamente.`);
                            }}
                            className="px-2.5 py-1 bg-red-700 hover:bg-red-800 text-white rounded text-[10px] font-black uppercase tracking-wider cursor-pointer"
                          >
                            Sim
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowCancelConfirm(false)}
                            className="px-2.5 py-1 bg-zinc-200 hover:bg-zinc-300 text-zinc-800 rounded text-[10px] font-extrabold cursor-pointer"
                          >
                            Não
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        id="btn-cancel-reservation-trigger"
                        onClick={() => setShowCancelConfirm(true)}
                        className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 border border-red-200 hover:border-red-300 font-bold text-xs uppercase tracking-wider rounded-lg transition-all cursor-pointer"
                      >
                        Cancelar reserva
                      </button>
                    )}
                    
                    {!showCancelConfirm && (
                      <button
                        type="button"
                        id="btn-realize-checkin-trigger"
                        onClick={() => {
                          setCheckInOption('automatic');
                          setCheckInErrorMsg(null);
                          setCheckInForceAllow(false);
                          setShowCheckInModal(true);
                        }}
                        className="px-5 py-2 bg-green-700 hover:bg-green-600 text-white font-bold text-xs uppercase tracking-wider rounded-lg transition-all cursor-pointer shadow-sm shadow-green-950/15"
                      >
                        Realizar Check-in
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Checked-in stay active status with add consumption block and check-out button */}
              {activeRes.status === 'Iniciado' && (
                <div className="space-y-6">
                  {/* Info check-in box */}
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-blue-50 border border-blue-200/50 rounded-xl">
                    <div className="flex items-start gap-2.5">
                      <Check className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                      <div>
                        <span className="text-xs font-bold text-blue-900 block">Dados do check-in</span>
                        <span className="text-xxs text-blue-750 font-medium block mt-0.5">
                          Realizado por: <strong className="font-extrabold">{activeRes.checkInDetails?.realizedBy || 'Administrador'}</strong> em {formatLocalDate(activeRes.checkInDetails?.date || '')} às {activeRes.checkInDetails?.time || '14:00'}.
                        </span>
                      </div>
                    </div>
                    <div>
                      <button
                        type="button"
                        id="btn-realize-checkout-trigger"
                        onClick={openCheckOutModalAction}
                        className="px-5 py-2 bg-green-700 hover:bg-green-600 text-white font-bold text-xs uppercase tracking-wider rounded-lg transition-all cursor-pointer shadow-sm shadow-green-950/15"
                      >
                        Realizar Check-out
                      </button>
                    </div>
                  </div>

                  {/* CUSTOM CONSUMPTION ENTRY: MINIBAR & ADD CUSTOM (Matching manual Pages 12 & 13) */}
                  <div className="border border-zinc-200 rounded-xl p-5 space-y-4">
                    <div className="border-b border-zinc-150 pb-2">
                      <h4 className="text-xs font-extrabold text-zinc-800 uppercase tracking-wider">Adicionar Consumo do cliente</h4>
                      <p className="text-[10px] text-zinc-400 mt-1 leading-normal">Lance os consumos de itens de frigobar ocorridos na acomodação do hóspede ou adicione taxas manuais adicionais de estadias.</p>
                    </div>

                    {consError && (
                      <div className="p-2 bg-red-50 text-red-700 text-xxs font-semibold rounded-md border border-red-100">
                        {consError}
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-1">
                      {/* Sub-panel 1: Drink minibar selections */}
                      <div className="p-3.5 bg-zinc-50 border border-zinc-200/50 rounded-xl space-y-3.5">
                        <span className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide">Consumo do Frigobar</span>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="col-span-2">
                            <label htmlFor="select-mb-item-cons" className="block text-[9px] font-bold text-zinc-400 uppercase mb-1">Selecione o Item do Frigobar</label>
                            <select
                              id="select-mb-item-cons"
                              value={consItemId}
                              onChange={(e) => setConsItemId(e.target.value)}
                              className="w-full text-xs p-1.5 border border-zinc-200 rounded bg-white text-zinc-700 font-semibold"
                            >
                              <option value="">-- Selecionar item --</option>
                              {(() => {
                                const mbar = minibars.find(m => m.accommodationId === activeRes.accommodationId && m.active);
                                if (!mbar) return <option value="" disabled>Sem frigobar cadastrado</option>;
                                if (!Array.isArray(mbar.items) || mbar.items.length === 0) {
                                  return <option value="" disabled>Frigobar vazio</option>;
                                }
                                return mbar.items.map(mbI => {
                                  const prd = stockItems.find(i => i.id === mbI.itemId);
                                  return (
                                    <option key={mbI.itemId} value={mbI.itemId}>
                                      {prd?.name || 'Item'} (Máx. {mbI.quantity})
                                    </option>
                                  );
                                });
                              })()}
                            </select>
                          </div>
                          <div>
                            <label htmlFor="input-mb-qty-cons" className="block text-[9px] font-bold text-zinc-400 uppercase mb-1">Quantidade</label>
                            <input
                              type="number"
                              id="input-mb-qty-cons"
                              value={consQty}
                              onChange={(e) => setConsQty(e.target.value)}
                              className="w-full text-xs p-1.5 border border-zinc-200 rounded bg-white text-center font-mono font-bold"
                              min={1}
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          id="btn-add-minibar-consumption"
                          onClick={handleAddMinibarConsumption}
                          className="w-full py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-xxs font-bold uppercase tracking-wider rounded transition-colors cursor-pointer"
                        >
                          Lançar Frigobar
                        </button>
                      </div>

                      {/* Sub-panel 2: Custom expenses, page 13 */}
                      <div className="p-3.5 bg-zinc-50 border border-zinc-200/50 rounded-xl space-y-3.5">
                        <span className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide">2. Consumo adicional (Outros Serviços)</span>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="col-span-2">
                            <label htmlFor="input-custom-cons-desc" className="block text-[9px] font-bold text-zinc-400 uppercase mb-1">Descrição</label>
                            <input
                              type="text"
                              id="input-custom-cons-desc"
                              value={consAddDesc}
                              onChange={(e) => setConsAddDesc(e.target.value)}
                              className="w-full text-xs p-1.5 border border-zinc-200 rounded bg-white text-zinc-700"
                              placeholder="Ex: Janela Quebrada, Jantar"
                            />
                          </div>
                          <div>
                            <label htmlFor="input-custom-cons-val" className="block text-[9px] font-bold text-zinc-400 uppercase mb-1">Valor Total (R$)</label>
                            <input
                              type="number"
                              id="input-custom-cons-val"
                              step="0.01"
                              value={consAddValue}
                              onChange={(e) => setConsAddValue(e.target.value)}
                              className="w-full text-xs p-1.5 border border-zinc-200 rounded bg-white text-center font-mono font-bold"
                              placeholder="159,99"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          id="btn-add-custom-consumption"
                          onClick={handleAddCustomConsumption}
                          className="w-full py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-xxs font-bold uppercase tracking-wider rounded transition-colors cursor-pointer"
                        >
                          Lançar Consumo adicional
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
               {/* Consumptions database row details */}
              <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
                <div className="border-b border-zinc-100 bg-zinc-50/50 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <h4 className="text-xs font-bold text-zinc-800 uppercase tracking-wider">Detalhamento dos Lançamentos</h4>
                  <span className="text-xs text-zinc-900 font-bold">
                    Consumo Total Registrado: <span className="text-red-650 font-mono font-extrabold">{formatCurrencyValue(activeRes.totalConsumptionAmount)}</span>
                  </span>
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table id="table-res-consumptions-historical" className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-zinc-50/50 border-b border-zinc-200 text-zinc-500 font-bold uppercase tracking-wider text-xxs">
                        <th className="py-2.5 px-4 w-12 text-center">#</th>
                        <th className="py-2.5 px-4">Item / Serviço</th>
                        <th className="py-2.5 px-4">Tipo Lançamento</th>
                        <th className="py-2.5 px-4">Qtd</th>
                        <th className="py-2.5 px-4">Vlr Unitário</th>
                        <th className="py-2.5 px-4">Total Cobrado (R$)</th>
                        <th className="py-2.5 px-4 text-center">Data Registro</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-150 font-medium text-zinc-700">
                      {!asArray(activeRes.consumptions).length ? (
                        <tr>
                          <td colSpan={7} className="p-6 text-center text-zinc-400 font-normal">
                            Nenhum consumo ou taxa registrada para esta hospedagem ainda.
                          </td>
                        </tr>
                      ) : (
                        asArray(activeRes.consumptions).map((c, idx) => (
                          <tr key={c.id || idx} className="hover:bg-zinc-50/30 transition-colors">
                            <td className="py-2 px-4 text-center text-zinc-400 font-mono text-xxs">{idx + 1}</td>
                            <td className="py-2 px-4 font-semibold text-zinc-900 text-xs">{c.description}</td>
                            <td className="py-2 px-4 font-normal text-zinc-500">
                              {c.type === 'frigobar' ? (
                                <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-100 px-1.5 py-0.2 rounded uppercase font-bold">Frigobar</span>
                              ) : (
                                <span className="text-[10px] bg-purple-50 text-purple-700 border border-purple-100 px-1.5 py-0.2 rounded uppercase font-bold">Serviço</span>
                              )}
                            </td>
                            <td className="py-2 px-4 font-mono">{c.quantity}</td>
                            <td className="py-2 px-4 font-mono">{formatCurrencyValue(c.price)}</td>
                            <td className="py-2 px-4 font-mono font-bold text-red-650">{formatCurrencyValue(c.total)}</td>
                            <td className="py-2 px-4 text-center font-mono text-[10px] text-zinc-400">{c.date || '-'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card List View */}
                <div className="md:hidden divide-y divide-zinc-150">
                  {!asArray(activeRes.consumptions).length ? (
                    <div className="p-6 text-center text-zinc-400 text-xs font-normal bg-white">
                      Nenhum consumo ou taxa de consumo registrada ainda.
                    </div>
                  ) : (
                    asArray(activeRes.consumptions).map((c, idx) => (
                      <div key={c.id || idx} className="p-3 bg-white space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-zinc-900 text-xs">{c.description}</span>
                          <span className="text-[10px] text-zinc-400 font-mono">Lançamento #{idx + 1}</span>
                        </div>
                        <div className="flex items-center justify-between text-xxs text-zinc-550">
                          <div>
                            {c.type === 'frigobar' ? (
                              <span className="bg-amber-50 text-amber-700 border border-amber-100 px-1 py-0.2 rounded uppercase font-bold text-[9px]">Frigobar</span>
                            ) : (
                              <span className="bg-purple-50 text-purple-700 border border-purple-100 px-1 py-0.2 rounded uppercase font-bold text-[9px]">Serviço</span>
                            )}
                          </div>
                          <span>Qtd: <strong className="font-mono text-zinc-800">{c.quantity} un</strong></span>
                          <span>Unit.: <strong className="font-mono text-zinc-800">{formatCurrencyValue(c.price)}</strong></span>
                        </div>
                        <div className="flex items-center justify-between border-t border-zinc-50 pt-1.5 text-xxs">
                          <span className="text-zinc-450 font-mono scale-[0.95] origin-left">{c.date || '-'}</span>
                          <span className="text-red-650 font-bold font-mono text-xs">Total: {formatCurrencyValue(c.total)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Box: If reservation is finished/closed (Finalizado), show final bills and a receipt reprensentative PDF button */}
              {activeRes.status === 'Finalizado' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl space-y-3.5 flex flex-col md:flex-row items-center justify-between gap-4">
                  <div>
                    <span className="text-xs font-bold text-green-900 block flex items-center gap-1">
                      <Check className="w-4 h-4 text-green-700" /> Check-out Realizado e Conta Quitada!
                    </span>
                    <span className="text-xxs text-green-750 block mt-0.5 max-w-lg">
                      Check-out executado administrativamente em <strong>{formatLocalDate(activeRes.checkOutDateActual || '')}</strong> às {activeRes.checkOutTimeActual || '11:55'}. Conta total final fechada no valor de <strong>{formatCurrencyValue(activeRes.finalTotal)}</strong>.
                    </span>
                  </div>
                  <button
                    type="button"
                    id="btn-print-receipt-finalizado"
                    onClick={() => generateCheckoutPDF(activeRes.id, activeRes.checkOutDateActual || getLocalDateString(), calculateNights(asObject(activeRes.checkInDetails)?.date || activeRes.checkInDate, activeRes.checkOutDateActual || getLocalDateString()), activeRes.totalStayAmount, activeRes.totalConsumptionAmount, asArray(activeRes.additionalCosts), activeRes.finalTotal)}
                    className="flex items-center gap-1 px-4.5 py-2.5 bg-zinc-900 hover:bg-zinc-850 text-white rounded-lg text-xs font-bold uppercase transition-transform active:scale-95 cursor-pointer shadow-sm shadow-zinc-905"
                  >
                    <FileDown className="w-4 h-4" /> Exportar 2ª Via do Recibo
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: AUTOMATIC OR MANUAL CHECK-IN POPUP (Page 11 & 12) */}
      {showCheckInModal && activeRes && (
        <div id="checkin-modal" className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-55 animate-fade-in overflow-y-auto">
          <div className="bg-white border border-zinc-200 shadow-xl rounded-xl max-w-sm lg:max-w-lg w-full max-h-[92vh] flex flex-col overflow-hidden transform transition-all my-auto">
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-zinc-300" />
                <h3 className="text-sm font-bold uppercase tracking-wider">Realizar Check-in</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowCheckInModal(false)}
                className="text-zinc-400 hover:text-white cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              <div className="space-y-3">
                <span className="block text-xxs font-bold text-zinc-400 uppercase tracking-wider">Escolha uma opção:</span>
                
                <label className="flex items-center gap-2 text-xs font-semibold text-zinc-700 cursor-pointer">
                  <input
                    type="radio"
                    name="check-in-mode"
                    checked={checkInOption === 'automatic'}
                    onChange={() => setCheckInOption('automatic')}
                    className="w-4 h-4 text-zinc-900"
                  />
                  <span>Check-In Automático (Recupera a data e hora atuais)</span>
                </label>

                <label className="flex items-center gap-2 text-xs font-semibold text-zinc-700 cursor-pointer">
                  <input
                    type="radio"
                    name="check-in-mode"
                    checked={checkInOption === 'manual'}
                    onChange={() => setCheckInOption('manual')}
                    className="w-4 h-4 text-zinc-900"
                  />
                  <span>Check-In Manual (Digite a data e hora manualmente)</span>
                </label>
              </div>

              {checkInOption === 'manual' && (
                <div id="manual-checkin-inputs" className="space-y-3 p-3 bg-zinc-50 border border-zinc-200 rounded-lg animate-fade-in-down">
                  <div>
                    <label htmlFor="input-manual-checkin" className="block text-xxs font-bold text-zinc-500 mb-0.5">Data do check-in *</label>
                    <input
                      type="date"
                      id="input-manual-checkin"
                      value={checkInManualDate}
                      onChange={(e) => setCheckInManualDate(e.target.value)}
                      className="w-full text-xs p-1.5 border border-zinc-200 rounded bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="input-manual-time" className="block text-xxs font-bold text-zinc-500 mb-0.5">Hora do check-in *</label>
                    <input
                      type="time"
                      id="input-manual-time"
                      value={checkInManualTime}
                      onChange={(e) => setCheckInManualTime(e.target.value)}
                      className="w-full text-xs p-1.5 border border-zinc-200 rounded bg-white"
                      required
                    />
                  </div>
                  <span className="text-[9px] text-zinc-400 block">* Apenas é possível realizar check-in manual com no máximo 1 dia de diferença.</span>
                </div>
              )}

              {checkInErrorMsg && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xxs text-amber-900 flex gap-2 animate-fade-in">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                  <div>
                    <strong className="font-bold uppercase block mb-0.5">Aviso de Auditoria:</strong>
                    <p className="leading-relaxed">{checkInErrorMsg}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCheckInModal(false)}
                  className="px-4 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-lg text-zinc-750 font-semibold text-xs cursor-pointer"
                >
                  Fechar
                </button>
                <button
                  type="button"
                  id="btn-confirm-checkin"
                  onClick={() => triggerCheckInSubmit()}
                  className="px-5 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider cursor-pointer shadow-sm shadow-green-950/10"
                >
                  Concluir Check-In
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: GORGEOUS CHECK-OUT ACCOUNT BILL PREVIEW (With dynamic late fees add, matching Page 13 & 14) */}
      {showCheckOutModal && activeRes && (
        <div id="checkout-calc-modal" className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-55 overflow-y-auto">
          <div className="bg-white border border-zinc-200 shadow-xl rounded-xl max-w-lg lg:max-w-2xl xl:max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden transform transition-all my-auto animate-fade-in shadow-zinc-800/10">
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-zinc-300" />
                <h3 className="text-sm font-bold uppercase tracking-wider">Detalhamento Check-out</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowCheckOutModal(false)}
                className="text-zinc-400 hover:text-white cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Calculations inside modal */}
            {(() => {
              const checkInDateActual = activeRes.checkInDetails?.date || activeRes.checkInDate;
              const checkOutToday = getLocalDateString();
              const elapsedNights = calculateNights(checkInDateActual, checkOutToday);
              const subtotalStay = elapsedNights * activeRes.dailyRate;
              const subtotalCons = activeRes.totalConsumptionAmount;
              const subtotalExtra = addedExtrasList.reduce((sum, i) => sum + i.amount, 0);
              const calculatedGrandTotal = subtotalStay + subtotalCons + subtotalExtra;

              return (
                <div className="p-4 sm:p-6 space-y-5 overflow-y-auto flex-1">
                  <div className="space-y-3.5">
                    {/* Consumidos count summary, Page 14 */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="block text-[10px] font-bold text-zinc-400 uppercase">Total de itens consumidos</span>
                        <input
                          type="text"
                          disabled
                          value={`${asArray(activeRes.consumptions).reduce((sum, item) => sum + item.quantity, 0)} lançamento(s)`}
                          className="w-full text-xs font-mono p-2 border border-zinc-150 rounded bg-zinc-100/60 font-semibold text-zinc-600 text-center"
                        />
                      </div>
                      <div>
                        <span className="block text-[10px] font-bold text-zinc-400 uppercase">Valor total consumido</span>
                        <input
                          type="text"
                          disabled
                          value={formatCurrencyValue(subtotalCons)}
                          className="w-full text-xs font-mono p-2 border border-zinc-150 rounded bg-zinc-100/60 font-bold text-zinc-800 text-center"
                        />
                      </div>
                    </div>

                    {/* Stay nights count summary, Page 14 */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="block text-[10px] font-bold text-zinc-400 uppercase">Total de diárias</span>
                        <input
                          type="text"
                          disabled
                          value={elapsedNights === 1 ? '1 diária' : `${elapsedNights} diárias`}
                          className="w-full text-xs font-mono p-2 border border-zinc-150 rounded bg-zinc-100/60 font-semibold text-zinc-650 text-center"
                        />
                      </div>
                      <div>
                        <span className="block text-[10px] font-bold text-zinc-400 uppercase">Valor total da diária</span>
                        <input
                          type="text"
                          disabled
                          value={formatCurrencyValue(subtotalStay)}
                          className="w-full text-xs font-mono p-2 border border-zinc-150 rounded bg-zinc-100/60 font-bold text-zinc-805 text-center"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Toggle Extra elements, Page 14 */}
                  <div className="space-y-2 p-3 bg-zinc-50 border border-zinc-200/50 rounded-xl">
                    <label className="flex items-center gap-2 text-xs font-bold text-zinc-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={extraCostsChecked}
                        onChange={(e) => setExtraCostsChecked(e.target.checked)}
                        className="w-4 h-4 text-zinc-900 focus:ring-0 rounded"
                      />
                      <span>Custos Adicionais</span>
                    </label>

                    {extraCostsChecked && (
                      <div className="space-y-3 pt-2 border-t border-zinc-200/60 animate-fade-in-down">
                        <div className="grid grid-cols-5 gap-2">
                          <input
                            type="number"
                            step="0.01"
                            value={tempExtraAmount}
                            onChange={(e) => setTempExtraAmount(e.target.value)}
                            className="col-span-2 text-xs border border-zinc-200 bg-white rounded p-1.5 text-center"
                            placeholder="R$ 50,00"
                          />
                          <input
                            type="text"
                            value={tempExtraDesc}
                            onChange={(e) => setTempExtraDesc(e.target.value)}
                            className="col-span-2 text-xs border border-zinc-200 bg-white rounded p-1.5"
                            placeholder="Ex. Multa por atraso"
                          />
                          <button
                            type="button"
                            onClick={handleAddCheckOutExtra}
                            className="bg-zinc-900 hover:bg-zinc-800 text-white font-bold p-1 rounded inline-flex items-center justify-center cursor-pointer shadow-sm"
                          >
                            +
                          </button>
                        </div>

                        {/* List output */}
                        {addedExtrasList.length > 0 && (
                          <div className="space-y-1 pt-1.5 max-h-24 overflow-y-auto">
                            {addedExtrasList.map((ext, idx) => (
                              <div key={ext.id} className="flex items-center justify-between text-xxs p-1.5 bg-white border border-zinc-100 rounded">
                                <span className="font-semibold text-zinc-700">{ext.description}</span>
                                <div className="flex items-center gap-1.5">
                                  <span className="font-mono font-bold text-red-550">{formatCurrencyValue(ext.amount)}</span>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveCheckOutExtra(idx)}
                                    className="text-red-500 hover:text-red-700 cursor-pointer font-bold text-[10px]"
                                  >
                                    Remover
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Grand total preview closed box */}
                  <div className="p-3 bg-zinc-900 text-white rounded-lg shadow-sm border border-zinc-950 flex items-center justify-between">
                    <span className="text-xxs font-extrabold uppercase tracking-widest text-zinc-400">Total Geral Fechamento</span>
                    <span className="font-mono text-lg font-extrabold text-yellow-400">{formatCurrencyValue(calculatedGrandTotal)}</span>
                  </div>

                  <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowCheckOutModal(false)}
                      className="px-4 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-lg text-zinc-700 font-semibold text-xs cursor-pointer"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      id="btn-confirm-checkout-payment"
                      onClick={handleConfirmPaymentAndCheckout}
                      className="flex items-center justify-center gap-1 px-5 py-2.5 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider cursor-pointer shadow-sm shadow-green-950/15"
                    >
                      <CreditCard className="w-4 h-4" /> Confirmar Pagamento
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
